import React, { useState } from "react";
import { parseInline } from "../../lib/guideMarkdown";
import { htmlToMarkdown, listTree, textBlocks } from "../../lib/richText";

/**
 * A requirement or description as it was written (28.10.2026): headings,
 * lists, bold, links and line breaks — drawn as elements, never as HTML.
 */
export function FormattedText({ text, className = "" }) {
  const blocks = textBlocks(text);
  if (!blocks.length) return null;
  return (
    <div className={`grid gap-3 [overflow-wrap:anywhere] ${className}`} data-testid="formatted-text">
      {blocks.map((b, i) => {
        if (b.type === "h2") return <h3 key={i} className="mt-1 text-[1.05em] font-bold text-[#373A45]"><Inline text={b.text} /></h3>;
        if (b.type === "h3") return <h4 key={i} className="font-semibold text-[#373A45]"><Inline text={b.text} /></h4>;
        if (b.type === "list") return <List key={i} list={listTree(b)} />;
        if (b.type === "callout") {
          return <blockquote key={i} className="border-l-2 border-[#965EA7]/40 pl-3 text-[#4B5160]"><Inline text={b.text} /></blockquote>;
        }
        if (b.type === "table") {
          return (
            <div key={i} className="overflow-x-auto rounded-lg border border-[#E5E7EB]">
              <table className="w-full border-collapse text-[0.93em]">
                <thead className="bg-[#F5F6F6] text-left">
                  <tr>{b.head.map((h, j) => <th key={j} className="px-3 py-1.5 font-semibold"><Inline text={h} /></th>)}</tr>
                </thead>
                <tbody>
                  {b.rows.map((row, r) => (
                    <tr key={r} className="border-t border-[#E5E7EB] align-top">
                      {row.map((c, j) => <td key={j} className="px-3 py-1.5"><Inline text={c} /></td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        if (b.type === "img") return <p key={i}>{b.alt || b.src}</p>;
        return (
          <p key={i}>
            {b.text.split("\n").map((line, j) => (
              <React.Fragment key={j}>{j > 0 && <br />}<Inline text={line} /></React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
}

function List({ list, nested = false }) {
  const Tag = list.ordered ? "ol" : "ul";
  const marker = list.ordered ? "list-decimal" : nested ? "list-[circle]" : "list-disc";
  return (
    <Tag className={`grid gap-1 pl-5 ${marker} marker:text-[#965EA7] ${nested ? "mt-1" : ""}`}>
      {list.items.map((it, j) => (
        <li key={j}><Inline text={it.text} />{it.sub && <List list={it.sub} nested />}</li>
      ))}
    </Tag>
  );
}

function Inline({ text }) {
  return parseInline(text).map((piece, i) => {
    if (piece.type === "strong") return <strong key={i} className="font-semibold">{piece.text}</strong>;
    if (piece.type === "em") return <em key={i}>{piece.text}</em>;
    if (piece.type === "code") return <code key={i} className="rounded bg-[#F5F6F6] px-1 py-0.5 text-[0.9em]">{piece.text}</code>;
    if (piece.type === "link") {
      return /^https?:\/\//.test(piece.href)
        ? <a key={i} href={piece.href} target="_blank" rel="noopener noreferrer" className="font-semibold text-[#7A4986] underline-offset-2 hover:underline">{piece.text}</a>
        : <React.Fragment key={i}>{piece.text}</React.Fragment>;
    }
    return <React.Fragment key={i}>{piece.text}</React.Fragment>;
  });
}

/**
 * A textarea that keeps formatting on paste: HTML on the clipboard (Claude,
 * Google Docs, Word) goes in as lists, headings and bold, as text. The text
 * stays editable as typed; the participant sees it formatted.
 */
export function FormattedTextarea({ value, onChange, maxLength, hint = true, ...rest }) {
  const [preview, setPreview] = useState(false);
  const onPaste = (e) => {
    const md = htmlToMarkdown(e.clipboardData?.getData("text/html"));
    if (!md) return;
    e.preventDefault();
    const el = e.currentTarget;
    // insertText keeps the browser's undo; the fallback sets the value.
    if (!(document.execCommand && document.execCommand("insertText", false, md))) {
      const next = `${value.slice(0, el.selectionStart)}${md}${value.slice(el.selectionEnd)}`;
      onChange(maxLength ? next.slice(0, maxLength) : next);
    }
  };
  return (
    <>
      <textarea {...rest} value={value} maxLength={maxLength} onChange={(e) => onChange(e.target.value)} onPaste={onPaste} />
      {hint && (
        <span className="text-xs font-normal text-[#6B7280]">
          Poți lipi text formatat, de exemplu din Claude: listele, titlurile și textul îngroșat se păstrează.
          Sau scrie tu: „- ” la început de rând face o listă, **așa** e îngroșat.{" "}
          <button type="button" onClick={() => setPreview((v) => !v)} disabled={!String(value || "").trim()}
            className="font-semibold text-[#7A4986] hover:underline disabled:opacity-50" data-testid="formatted-preview-toggle">
            {preview ? "Ascunde previzualizarea" : "Vezi cum arată"}
          </button>
        </span>
      )}
      {preview && String(value || "").trim() && (
        <div className="rounded-xl border border-[#E5E7EB] bg-white p-4 text-sm font-normal leading-relaxed text-[#373A45]"
          data-testid="formatted-preview">
          <FormattedText text={value} />
        </div>
      )}
    </>
  );
}
